import { ReactElement } from "react";
import { DndProvider } from "react-dnd";
import {
  Tree,
  MultiBackend,
  getBackendOptions,
  NodeModel,
  RenderParams,
} from "@minoru/react-dnd-treeview";

import * as Styled from "./styled";
export type TreeData<T> = NodeModel<T>[];

interface RenderParamsWithEnabled extends RenderParams {
  dragAndDropEnabled: boolean;
}

type Props<T> = {
  treeData: TreeData<T> | undefined;
  onChange: (arg1: TreeData<T>) => void;
  dragAndDropEnabled: boolean;
  renderItem: (node: NodeModel<T>, params: RenderParamsWithEnabled) => ReactElement;
  /** Extra drop rule. Default rules (reorder mode, no folder-in-folder) still apply. */
  canDrop?: (
    dragSource: NodeModel<T> | undefined,
    dropTarget: NodeModel<T> | undefined,
  ) => boolean;
};

function OverviewDragAndDrop<T>({
  dragAndDropEnabled,
  onChange,
  treeData,
  renderItem,
  canDrop,
}: Props<T>) {
  const handleDrop = (newTree: TreeData<T>) => {
    onChange(newTree);
  };

  if (!treeData) return null;

  return (
    <Styled.Container>
      <DndProvider backend={MultiBackend} options={getBackendOptions()}>
        <Tree
          enableAnimateExpand
          tree={treeData}
          rootId={0}
          render={(node, params) => renderItem(node, { ...params, dragAndDropEnabled })}
          onDrop={handleDrop}
          classes={{
            root: "treeRoot",
            draggingSource: "dragging-source",
            dropTarget: "drop-target",
            placeholder: "placeholder-container",
          }}
          sort={false}
          insertDroppableFirst={false}
          canDrag={() => dragAndDropEnabled}
          canDrop={(_, { dragSource, dropTarget }) => {
            if (!dragAndDropEnabled) return false;
            // Disabler folder in folder
            if (dragSource?.droppable && dropTarget?.droppable) return false;
            if (dropTarget && !dropTarget.droppable) return false;
            if (canDrop && !canDrop(dragSource, dropTarget)) return false;
            return true;
          }}
          dropTargetOffset={10}
          placeholderRender={() => <Styled.PlaceHolder />}
        />
      </DndProvider>
    </Styled.Container>
  );
}

export default OverviewDragAndDrop;
